/**
 * CalendarPopover — the month calendar the date field opens. Days before `min`
 * are disabled; the chosen day and today are marked. It never takes focus from
 * the field's input on a click, so picking a day reads as one gesture.
 */
import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import { viewerDate } from '../../../utils/viewer-date';

import { monthCells, monthOf, shiftMonth, type MonthCursor } from './calendar-month';
import { CalendarDay } from './CalendarDay';
import { PICKER_POPOVER } from './picker-popover-style';
import { keepInputFocus } from './use-picker-field';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const NAV_BUTTON: React.CSSProperties = {
  width: 26,
  height: 26,
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--nb-ink-2)',
  cursor: 'pointer',
  fontSize: 14,
};

export function CalendarPopover({
  value,
  min,
  onPick,
}: {
  value: string;
  min?: string;
  onPick: (value: string) => void;
}) {
  const start = monthOf(value) ?? monthOf(min ?? '') ?? (monthOf(viewerDate()) as MonthCursor);
  const [cursor, setCursor] = useState<MonthCursor>(start);
  // Typing a date in another month moves the calendar there.
  const valueMonth = value.slice(0, 7);
  useEffect(() => {
    const typedMonth = monthOf(`${valueMonth}-01`);
    if (typedMonth) setCursor(typedMonth);
  }, [valueMonth]);
  const title = format(new Date(cursor.year, cursor.month - 1, 1), 'MMMM yyyy');
  return (
    <div
      role="dialog"
      aria-label="Choose a date"
      onMouseDown={keepInputFocus}
      style={{ ...PICKER_POPOVER, width: 240 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <button
          type="button"
          aria-label="Previous month"
          style={NAV_BUTTON}
          onClick={() => setCursor((c) => shiftMonth(c, -1))}
        >
          ‹
        </button>
        <span style={{ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 700 }}>{title}</span>
        <button
          type="button"
          aria-label="Next month"
          style={NAV_BUTTON}
          onClick={() => setCursor((c) => shiftMonth(c, 1))}
        >
          ›
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {WEEKDAYS.map((d, i) => (
          <span
            key={`${d}${i}`}
            aria-hidden="true"
            style={{
              textAlign: 'center',
              fontSize: 10,
              color: 'var(--nb-subtle)',
              fontWeight: 700,
            }}
          >
            {d}
          </span>
        ))}
        {monthCells(cursor).map((day, i) =>
          day ? (
            <CalendarDay key={day} value={day} selected={day === value} min={min} onPick={onPick} />
          ) : (
            <span key={`blank-${i}`} />
          ),
        )}
      </div>
    </div>
  );
}
